import { LEGACY_PATH_PREFIXES, LegacyRedirect } from "@/components/LegacyRedirect";
import { SiteSwitcher } from "@/components/SiteSwitcher";
import { StaleBundleBanner } from "@/components/StaleBundleBanner";
import { Toaster } from "@/components/Toaster";
import { AUTH_URL, mediaApi, sharedApi, trafficApi } from "@/lib/api";
import { AuthProvider, useAuth } from "@/lib/auth";
import { features } from "@/lib/features";
import { SiteProvider } from "@/lib/site-context";
import { toast } from "@/lib/toast";
import { Dashboard } from "@/pages/Dashboard";
import { Setup2FA } from "@/pages/Setup2FA";
import { useQueryClient } from "@tanstack/react-query";
import { AppShell } from "@theg1group/app-shell";
import { type FeatureContext, FeatureCtx } from "@theg1group/feature-kit";
import { NotificationBell } from "@theg1group/pwa-kit";
import { PagesRegistryProvider } from "@theg1group/rbac-ui";
import { DevInfoOverlay } from "@theg1group/ui";
import { LayoutDashboard } from "lucide-react";
import { Suspense, useEffect } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";

// Local-only gate for /setup-2fa — sits OUTSIDE the AppShell (whose RequireAuth
// would bounce a not-yet-2FA user away from the setup page).
function Setup2FARoute({ children }: { children: React.ReactNode }) {
	const { user, isLoading } = useAuth();
	if (isLoading)
		return (
			<div className="flex min-h-screen items-center justify-center">
				<p className="text-muted">Loading...</p>
			</div>
		);
	if (!user) return null;
	if (user?.totpEnabled) return <Navigate to="/" replace />;
	if (user?.role === "viewer") return <Navigate to="/" replace />;
	return <>{children}</>;
}

function FeatureProvider({ children }: { children: React.ReactNode }) {
	const { user } = useAuth();
	const queryClient = useQueryClient();

	const ctx: FeatureContext = {
		api: { shared: sharedApi, media: mediaApi, traffic: trafficApi },
		user: {
			id: user?.id ?? "",
			email: user?.email ?? "",
			name: user?.name ?? undefined,
			role: user?.role ?? "",
			permissions: user?.permissions ? [...user.permissions] : [],
			sitePermissions: user?.sitePermissions ?? {},
			appAccess: user?.appAccess ? [...user.appAccess] : undefined,
			avatarUrl: user?.avatarUrl ?? undefined,
		},
		toast,
		queryClient,
	};

	return <FeatureCtx value={ctx}>{children}</FeatureCtx>;
}

function DevInfoWrapper() {
	const { user } = useAuth();
	const location = useLocation();
	return (
		<DevInfoOverlay
			isAdmin={user?.role === "admin"}
			appId="console"
			currentPath={location.pathname}
			enableHealthCheck
		/>
	);
}

/** Notifications bell for the shell topbar (router-aware deep-link + SW bridge). */
function ConsoleNotifications() {
	const navigate = useNavigate();
	useEffect(() => {
		if (!("serviceWorker" in navigator)) return;
		const onMessage = (e: MessageEvent) => {
			const d = e.data as { type?: string; url?: string } | undefined;
			if (d?.type === "g1:navigate" && d.url) navigate(d.url);
		};
		navigator.serviceWorker.addEventListener("message", onMessage);
		return () => navigator.serviceWorker.removeEventListener("message", onMessage);
	}, [navigate]);
	return <NotificationBell appId="app-console" onNavigate={(url) => navigate(url)} />;
}

export function App() {
	return (
		<BrowserRouter>
			<AuthProvider>
				<PagesRegistryProvider
					appId="console"
					fetchRegistry={async () => {
						const res = await fetch(`${AUTH_URL}/pages-registry`, {
							credentials: "include",
						});
						if (!res.ok) throw new Error(`pages-registry ${res.status}`);
						return (await res.json()) as {
							data: import("@theg1group/rbac-ui").RegistryPage[];
						};
					}}
				>
					<SiteProvider>
						<StaleBundleBanner />
						<Toaster />
						<DevInfoWrapper />
						<Routes>
							<Route
								path="/setup-2fa"
								element={
									<Setup2FARoute>
										<Setup2FA />
									</Setup2FARoute>
								}
							/>

							<Route
								path="/*"
								element={
									<AppShell
										appId="console"
										requireAppAccess="console"
										authUrl={AUTH_URL}
										features={features}
										branding={{ title: "G1 Console", Icon: LayoutDashboard }}
										topbarActions={<SiteSwitcher collapsed={false} />}
										notifications={<ConsoleNotifications />}
										collapsibleGroups
									>
										<FeatureProvider>
											<Routes>
												<Route path="/" element={<Dashboard />} />
												{features.flatMap((f) =>
													f.routes.map((r) => (
														<Route
															key={r.path}
															path={r.path}
															element={
																<Suspense fallback={<div className="p-8 text-muted">Loading...</div>}>
																	<r.component />
																</Suspense>
															}
														/>
													)),
												)}
												{LEGACY_PATH_PREFIXES.flatMap((prefix) => [
													<Route key={prefix} path={prefix} element={<LegacyRedirect />} />,
													<Route
														key={`${prefix}/*`}
														path={`${prefix}/*`}
														element={<LegacyRedirect />}
													/>,
												])}
												<Route path="*" element={<Navigate to="/" replace />} />
											</Routes>
										</FeatureProvider>
									</AppShell>
								}
							/>
						</Routes>
					</SiteProvider>
				</PagesRegistryProvider>
			</AuthProvider>
		</BrowserRouter>
	);
}
